import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/ui/PageHeader';
import { usePageMeta } from '@/hooks/usePageMeta';
import type { BlogSummary } from '@/types/profile';
import './BlogsPage.css';

export default function BlogsPage() {
  usePageMeta(
    'Garden Blogs',
    'Garden diaries and growing notes from members of The Gardeners Calendar community.',
  );
  const { isAuthenticated, loading } = useAuth();
  const [blogs, setBlogs] = useState<BlogSummary[]>([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    fetch('/api/blogs')
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setBlogs(data))
      .catch(() => setError('Could not load blogs. Please try again.'))
      .finally(() => setFetching(false));
  }, [isAuthenticated, loading]);

  if (loading || fetching) {
    return (
      <div className="container blogs-loading">
        <span style={{ fontSize: '2.5rem' }}>🌿</span>
        <p className="text-hand" style={{ color: 'var(--sage)' }}>Loading blogs…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container blogs-gate">
        <p style={{ fontSize: '3rem' }}>📔</p>
        <h2 className="text-headline">Member blogs</h2>
        <p className="text-body" style={{ color: 'var(--ink-light)', marginBottom: 'var(--space-6)' }}>
          Sign in to read garden diaries and blogs from members of The Gardeners' Calendar.
        </p>
        <Link to="/login" className="btn-sage">Sign in to continue</Link>
      </div>
    );
  }

  return (
    <div className="blogs-page">
      <PageHeader
        title="Garden Blogs"
        subtitle="Diaries, growing notes and seasonal reflections from members"
        eyebrow="Community"
        decorationEmoji="📔"
      />

      <div className="container section">
        {error && (
          <p style={{ color: 'var(--ink-light)', textAlign: 'center' }}>{error}</p>
        )}

        {!error && blogs.length === 0 && (
          <div style={{ textAlign: 'center', padding: 'var(--space-20) 0' }}>
            <p style={{ fontSize: '3rem', marginBottom: 'var(--space-4)' }}>🌱</p>
            <p className="text-subtitle" style={{ color: 'var(--ink-light)' }}>
              No blogs yet. Be the first — set up your blog in My Garden → Profile.
            </p>
          </div>
        )}

        <div className="blogs-grid">
          {blogs.map(b => (
            <Link key={b.userId} to={`/blogs/${b.blog.slug}`} className="blog-card paper">
              <div className="blog-card-author">
                {b.photoUrl
                  ? <img src={b.photoUrl} alt={b.displayName} className="blog-card-avatar" />
                  : <div className="blog-card-avatar blog-card-avatar--placeholder">🌿</div>
                }
                <div>
                  <p className="text-title" style={{ marginBottom: 2 }}>{b.nickname || b.displayName}</p>
                  {b.location && <p className="text-caption" style={{ color: 'var(--ink-faint)' }}>📍 {b.location}</p>}
                </div>
              </div>
              <h3 className="text-title blog-card-title">{b.blog.title}</h3>
              <span className="blog-card-cta text-caption">Read blog →</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
